import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { UserContext } from '../App';
import { supabase } from '../utils/supabaseClient';
import { getLevelInfo } from '../utils/levels';
import { getMbtiStyle } from '../utils/mbti';
import { MapPin, Briefcase, Calendar, Loader2, MoreVertical, Flag, Ban, UserCheck, User, Sparkles } from 'lucide-react';
import { getZodiacSign, calculateAge, isBirthdayToday } from '../utils/zodiac';
import { useModal } from '../context/ModalContext';
import ReportModal from '../components/common/ReportModal';
import { BackButton } from '../components/common/PageHeader';
import { useIsDesktop } from '../hooks/useIsDesktop';
import { AvatarDisplay } from '../utils/avatars';
import LoginPromptModal from '../components/common/LoginPromptModal';

export default function UserDetail() {
  const isDesktop = useIsDesktop();
  const { userId } = useParams();
  const navigate = useNavigate();
  const { currentUser, accessToken, setBlockedIds } = useContext(UserContext);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [, setUserEvents] = useState([]);
  const [showMenu, setShowMenu] = useState(false);
  const [isLoginPromptOpen, setIsLoginPromptOpen] = useState(false);
  const { showConfirm, showAlert } = useModal();
  const [showReportModal, setShowReportModal] = useState(false);
  const [friendship, setFriendship] = useState(null);
  const menuRef = useRef(null);

  const isOwnProfile = currentUser && user && (currentUser.id === user.id || currentUser._id === user.id);

  useEffect(() => {
    const loadUserData = async () => {
      try {
        setIsLoading(true);

        const { data: userProfile, error: userError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', userId)
          .single();

        if (userError || !userProfile) throw new Error('Failed to fetch user data');

        setUser({ _id: userProfile.id, id: userProfile.id, auth0Id: userProfile.id, profile: userProfile });

        const { data: allEvents, error: eventsError } = await supabase
          .from('events')
          .select('*, event_participants(user_id, status)');

        if (!eventsError && allEvents) {
          const userParticipatedEvents = allEvents.filter(event => {
            const approvedParticipants = (event.event_participants || [])
              .filter(ep => ep.status === 'approved')
              .map(ep => ep.user_id);
            const organizerId = event.organizer_id;
            return approvedParticipants.includes(userId) || organizerId === userId;
          });
          setUserEvents(userParticipatedEvents);
        }

        // Fetch friendship status if logged in
        if (currentUser?.id || currentUser?._id) {
          const currentUserId = currentUser.id || currentUser._id;
          const { data: fData, error: fError } = await supabase
            .from('friendships')
            .select('*')
            .or(`and(user_id.eq.${currentUserId},friend_id.eq.${userId}),and(user_id.eq.${userId},friend_id.eq.${currentUserId})`)
            .maybeSingle();

          if (!fError && fData) {
            setFriendship(fData);
          }
        }
      } catch (error) {
        console.error('Error loading user data:', error);
        showAlert('錯誤', '載入用戶資料失敗');
        navigate(-1);
      } finally {
        setIsLoading(false);
      }
    };

    if (userId) {
      loadUserData();
    }
  }, [userId, navigate, currentUser, showAlert]);

  const handleBlockUser = async () => {
    setShowMenu(false);

    try {
      if (!currentUser || (!currentUser.id && !currentUser._id)) throw new Error('User not authenticated');
      const currentUserId = currentUser.id || currentUser._id;

      if (currentUserId === userId) {
        showAlert('提示', '您不能封鎖自己。');
        return;
      }

      showConfirm('封鎖用戶', '確定要封鎖此對象？封鎖後雙方將無法看到彼此。', async () => {
        try {
          const { error } = await supabase
            .from('blocked_users')
            .insert({
              blocker_id: currentUserId,
              blocked_id: userId
            });

          if (error) {
            if (error.code === '23505') { // Unique violation
              showAlert('提示', '您已經封鎖過此對象。');
            } else {
              console.error('Supabase error:', error);
              showAlert('錯誤', `封鎖失敗: ${error.message || '未知錯誤'}`);
            }
            return;
          } else {
            if (setBlockedIds) {
              setBlockedIds(prev => Array.from(new Set([...(prev || []), userId])));
            }
            showAlert('成功', '已成功封鎖此對象。');
          }
          navigate(-1);
        } catch (error) {
          console.error('封鎖失敗:', error);
          showAlert('錯誤', `封鎖發生錯誤: ${error.message || '請稍後再試'}`);
        }
      });
    } catch (error) {
      console.error('封鎖預處理失敗:', error);
      showAlert('錯誤', `處理發生錯誤: ${error.message || '請稍後再試'}`);
    }
  };

  const handleAddFriend = async () => {
    try {
      const currentUserId = currentUser?.id || currentUser?._id;
      if (!currentUserId) {
        setIsLoginPromptOpen(true);
        return;
      }

      const { error } = await supabase.from('friendships').insert({
        user_id: currentUserId,
        friend_id: userId,
        requested_by: currentUserId,
        status: 'pending'
      });

      if (error) throw error;

      showAlert('成功', "好友邀請已送出");
      // Update local state
      setFriendship({
        user_id: currentUserId,
        friend_id: userId,
        status: 'pending'
      });
    } catch (error) {
      console.error("送出邀請失敗:", error);
      showAlert('錯誤', error.message || "送出邀請失敗");
    }
  };

  const handleAcceptFriend = async () => {
    try {
      const { error } = await supabase
        .from('friendships')
        .update({ status: 'accepted', accepted_at: new Date().toISOString() })
        .eq('id', friendship.id);

      if (error) throw error;

      showAlert('成功', "已接受好友邀請");
      setFriendship(prev => ({ ...prev, status: 'accepted' }));
    } catch (error) {
      console.error("接受邀請失敗:", error);
      showAlert('錯誤', error.message || "接受邀請失敗");
    }
  };

  const handleCancelOrRemoveFriend = async (isCancel) => {
    try {
      const { error } = await supabase
        .from('friendships')
        .delete()
        .eq('id', friendship.id);

      if (error) throw error;

      showAlert('提示', isCancel ? "已取消好友邀請" : "已解除好友關係");
      setFriendship(null);
    } catch (error) {
      console.error("操作失敗:", error);
      showAlert('錯誤', error.message || "操作失敗");
    }
  };

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '80vh'
      }}>
        <Loader2 style={{ width: '24px', height: '24px', animation: 'spin 1s linear infinite' }} />
        <span style={{ fontSize: '1.2rem', marginLeft: '12px' }}>載入中...</span>
      </div>
    );
  }

  if (!user) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '80vh',
        flexDirection: 'column'
      }}>
        <h2>找不到用戶</h2>
        <button
          onClick={() => navigate(-1)}
          style={{
            marginTop: '16px',
            padding: '12px 24px',
            backgroundColor: '#4e8bca',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer'
          }}
        >
          返回
        </button>
      </div>
    );
  }

  const profile = user.profile || {};

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #eef4f9 0%, #f5f9fc 18%, #ffffff 48%)',
      paddingBottom: '80px',
      overflowX: 'clip'
    }}>
      {/* Simple Header */}
      <div style={{
        padding: '16px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        maxWidth: isDesktop ? '760px' : '100%',
        margin: '0 auto'
      }}>
        <BackButton onClick={() => navigate(-1)} label="返回上一頁" />
        
        {/* More menu — only show on other users' profiles */}
        {!isOwnProfile && (
          <div ref={menuRef} style={{ position: 'relative' }}>
            <button
              onClick={() => {
                if (!currentUser) {
                  setIsLoginPromptOpen(true);
                  return;
                }
                setShowMenu(v => !v);
              }}
              style={{
                background: 'rgba(255,255,255,0.7)',
                backdropFilter: 'blur(8px)',
                border: 'none',
                borderRadius: '50%',
                width: '36px', height: '36px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer',
              }}
            >
              <MoreVertical size={18} color="#374151" />
            </button>

            {showMenu && (
              <div style={{
                position: 'absolute', top: '44px', right: 0,
                background: 'white',
                borderRadius: '12px',
                boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
                border: '1px solid #f1f5f9',
                minWidth: '150px',
                overflow: 'hidden',
                zIndex: 100,
              }}>
                <button
                  onClick={() => { setShowMenu(false); setShowReportModal(true); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '10px',
                    width: '100%', padding: '13px 16px',
                    background: 'transparent', border: 'none',
                    borderBottom: '1px solid #f1f5f9',
                    color: '#ef4444', fontWeight: '600', fontSize: '0.9rem',
                    cursor: 'pointer', textAlign: 'left',
                  }}
                >
                  <Flag size={16} />
                  檢舉此用戶
                </button>
                <button
                  onClick={handleBlockUser}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '10px',
                    width: '100%', padding: '13px 16px',
                    background: 'transparent', border: 'none',
                    color: '#333', fontWeight: '600', fontSize: '0.9rem',
                    cursor: 'pointer', textAlign: 'left',
                  }}
                >
                  <Ban size={16} />
                  封鎖此對象
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Main Container */}
      <div style={{
        maxWidth: isDesktop ? '760px' : '100%',
        margin: '0 auto',
        padding: '0 16px',
        position: 'relative',
        zIndex: 5
      }}>
        {/* Profile Card */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '24px',
          padding: isDesktop ? '40px' : '32px 24px',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.08)',
          marginBottom: '24px',
          position: 'relative'
        }}>
          {/* Friend Icon Top Right */}
          {friendship?.status === 'accepted' && (
            <div style={{
              position: 'absolute',
              top: '24px',
              right: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '36px',
              height: '36px',
              backgroundColor: '#eef4f9',
              borderRadius: '50%',
              color: '#376ba1',
              boxShadow: '0 4px 10px rgba(78, 139, 202, 0.2)'
            }} title="已是好友">
              <UserCheck size={18} />
            </div>
          )}

          {/* Top Info Layout */}
          <div style={{
             display: 'flex',
             flexDirection: isDesktop ? 'row' : 'column',
             alignItems: isDesktop ? 'flex-start' : 'center',
             gap: '24px',
             marginBottom: '24px'
          }}>
             {/* Avatar */}
             <div>
                <div style={{
                  padding: '4px',
                  background: 'white',
                  borderRadius: '50%',
                  display: 'inline-flex',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.06)'
                }}>
                  <AvatarDisplay avatarId={profile.avatar || 'avatar1'} size={isDesktop ? 100 : 90} />
                </div>
             </div>

             {/* Info Column */}
             <div style={{ flex: 1, textAlign: isDesktop ? 'left' : 'center', width: '100%', marginTop: isDesktop ? '8px' : 0 }}>
               <div style={{ display: 'flex', alignItems: 'center', justifyContent: isDesktop ? 'flex-start' : 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '12px' }}>
                  <h1 style={{
                    fontSize: isDesktop ? '2rem' : '1.75rem',
                    fontWeight: '700',
                    color: '#1a1a1a',
                    margin: 0
                  }}>
                    {profile.name || '未知用戶'}
                  </h1>
                  {(() => {
                    const mbtiStyle = getMbtiStyle(profile.mbti);
                    return mbtiStyle && (
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: '4px 12px',
                        borderRadius: '10px',
                        background: mbtiStyle.bg,
                        color: mbtiStyle.color,
                        fontSize: '13px',
                        fontWeight: '700',
                        letterSpacing: '0.05em'
                      }}>
                        {profile.mbti}
                      </span>
                    );
                  })()}
                  {profile.badge === 'founding' && (
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '4px 12px',
                      borderRadius: '14px',
                      background: 'linear-gradient(135deg, #eef4f9, #fde68a)',
                      border: '1px solid #f59e0b',
                      fontSize: '13px',
                      fontWeight: '600',
                      color: '#92400e'
                    }}>
                      🏅 創始會員
                    </span>
                  )}
                  {(() => {
                    const levelInfo = getLevelInfo(profile.total_points || 0);
                    return (
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '4px 12px',
                        borderRadius: '999px',
                        background: '#eef4f9',
                        border: '1px solid #4e8bca',
                        fontSize: '13px',
                        fontWeight: '600',
                        color: '#4e8bca'
                      }}>
                        LV.{levelInfo.level}・{levelInfo.title}
                      </span>
                    );
                  })()}
               </div>

               <div style={{
                 display: 'flex',
                 alignItems: 'center',
                 justifyContent: isDesktop ? 'flex-start' : 'center',
                 gap: '8px',
                 color: '#6b7280',
                 fontSize: '0.95rem',
                 flexWrap: 'wrap',
                 lineHeight: '1.6'
               }}>
              {profile.gender && (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <User size={16} />
                    <span>{profile.gender === 'male' ? '男性' : profile.gender === 'female' ? '女性' : profile.gender}</span>
                  </div>
                  <span style={{ color: '#d1d5db' }}>•</span>
                </>
              )}

              {(profile.birthday || profile.age) && (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Calendar size={16} />
                    <span>
                      {(() => {
                        const age = profile.birthday ? calculateAge(profile.birthday) : profile.age;
                        return `${age} 歲`;
                      })()}
                    </span>
                  </div>

                  {profile.birthday && getZodiacSign(profile.birthday) && (
                    <>
                      <span style={{ color: '#d1d5db' }}>•</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Sparkles size={16} color="#8b5cf6" />
                        <span>{getZodiacSign(profile.birthday).name}</span>
                      </div>
                    </>
                  )}
                  {profile.birthday && isBirthdayToday(profile.birthday) && <span>🎂</span>}
                </>
              )}

              {profile.city && (
                <>
                  {(profile.birthday || profile.age || profile.gender) && <span style={{ color: '#d1d5db' }}>•</span>}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <MapPin size={16} />
                    <span>{profile.city}</span>
                  </div>
                </>
              )}

              {profile.occupation && (
                <>
                  <span style={{ color: '#d1d5db' }}>•</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Briefcase size={16} />
                    <span>{profile.occupation}</span>
                  </div>
                </>
              )}
            </div>
          </div>
          </div>

          {/* About Me Section */}
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{
              fontSize: '1.25rem',
              fontWeight: '600',
              color: '#1a1a1a',
              margin: '0 0 12px 0'
            }}>
              關於我
            </h3>
            <p style={{
              color: '#6b7280',
              fontSize: '0.95rem',
              lineHeight: '1.7',
              margin: 0,
              whiteSpace: 'pre-wrap'
            }}>
              {profile.bio && profile.bio !== '未填寫'
                ? profile.bio
                : '此人未填寫自我介紹'
              }
            </p>
          </div>

          {/* Personal Traits */}
          {profile.traits && profile.traits.length > 0 && (
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{
                fontSize: '1.25rem',
                fontWeight: '600',
                color: '#1a1a1a',
                margin: '0 0 12px 0'
              }}>
                個人特質
              </h3>
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px'
              }}>
                {profile.traits.map((trait, index) => (
                  <span
                    key={index}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '20px',
                      backgroundColor: '#eef4f9',
                      color: '#376ba1',
                      fontSize: '0.875rem',
                      fontWeight: '500'
                    }}
                  >
                    {trait}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Interests */}
          {profile.interests && profile.interests.length > 0 && (
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{
                fontSize: '1.25rem',
                fontWeight: '600',
                color: '#1a1a1a',
                margin: '0 0 12px 0'
              }}>
                興趣愛好
              </h3>
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px'
              }}>
                {profile.interests.map((interest, index) => (
                  <span
                    key={index}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '20px',
                      backgroundColor: '#f3f4f6',
                      color: '#374151',
                      fontSize: '0.875rem',
                      fontWeight: '500'
                    }}
                  >
                    {interest}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Participated Events (Hidden per user request) */}
          {/*
          {userEvents.length > 0 && (
            <div>
              <h3 style={{
                fontSize: '1.25rem',
                fontWeight: '600',
                color: '#1a1a1a',
                margin: '0 0 12px 0'
              }}>
                參與的活動
              </h3>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
              }}>
                {userEvents.slice(0, 5).map((event) => (
                  <div
                    key={event.id}
                    style={{
                      padding: '12px 16px',
                      backgroundColor: '#f9fafb',
                      borderRadius: '12px',
                      border: '1px solid #e5e7eb'
                    }}
                  >
                    <div style={{
                      fontWeight: '600',
                      color: '#1f2937',
                      fontSize: '0.95rem',
                      marginBottom: '4px'
                    }}>
                      {event.title}
                    </div>
                    <div style={{
                      color: '#6b7280',
                      fontSize: '0.85rem'
                    }}>
                      {new Date(event.date).toLocaleDateString('zh-TW')}
                    </div>
                  </div>
                ))}
                {userEvents.length > 5 && (
                  <div style={{
                    textAlign: 'center',
                    color: '#6b7280',
                    fontSize: '0.875rem'
                  }}>
                    還有 {userEvents.length - 5} 個活動...
                  </div>
                )}
              </div>
            </div>
          )}
          */}

          {/* Friendship Action Button Moved to Bottom */}
          {!isOwnProfile && (
            <div style={{ marginTop: '32px' }}>
              {(() => {
                const currentUserId = currentUser?.id || currentUser?._id;

                if (!friendship || friendship.status === 'declined' || !currentUser) {
                  return (
                    <button
                      onClick={handleAddFriend}
                      style={{
                        width: '100%', padding: '14px', borderRadius: '16px', border: 'none',
                        background: '#376ba1', color: 'white', fontSize: '1rem', fontWeight: '600',
                        cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
                      }}
                    >
                      加為好友
                    </button>
                  );
                }

                if (friendship.status === 'accepted') {
                  return (
                    <button
                      onClick={() => {
                        showConfirm('解除好友', '確定要解除好友關係嗎？', () => handleCancelOrRemoveFriend(false));
                      }}
                      style={{
                        width: '100%', padding: '14px', borderRadius: '16px', border: 'none',
                        background: '#eef4f9', color: '#374151', fontSize: '1rem', fontWeight: '600',
                        cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
                      }}
                    >
                      已是好友 (點擊解除)
                    </button>
                  );
                }

                if (friendship.status === 'pending') {
                  // Check if current user is the one who received the request
                  if (friendship.friend_id === currentUserId) {
                    return (
                      <div style={{ display: 'flex', gap: '12px' }}>
                        <button
                          onClick={handleAcceptFriend}
                          style={{
                            flex: 1, padding: '14px', borderRadius: '16px', border: 'none',
                            background: '#059669', color: 'white', fontSize: '1rem', fontWeight: '600',
                            cursor: 'pointer', transition: 'all 0.2s'
                          }}
                        >
                          接受好友
                        </button>
                        <button
                          onClick={() => handleCancelOrRemoveFriend(true)}
                          style={{
                            flex: 1, padding: '14px', borderRadius: '16px', border: 'none',
                            background: '#f3f4f6', color: '#4b5563', fontSize: '1rem', fontWeight: '600',
                            cursor: 'pointer', transition: 'all 0.2s'
                          }}
                        >
                          拒絕
                        </button>
                      </div>
                    );
                  } else {
                    return (
                      <button
                        onClick={() => handleCancelOrRemoveFriend(true)}
                        style={{
                          width: '100%', padding: '14px', borderRadius: '16px', border: 'none',
                          background: '#f59e0b', color: 'white', fontSize: '1rem', fontWeight: '600',
                          cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
                        }}
                      >
                        收回邀請
                      </button>
                    );
                  }
                }
              })()}
            </div>
          )}
        </div>
      </div>

      {/* Report Modal */}
      {showReportModal && (
        <ReportModal
          targetUser={user}
          accessToken={accessToken}
          reporterId={currentUser?.id || currentUser?._id}
          onClose={() => setShowReportModal(false)}
        />
      )}

      <LoginPromptModal isOpen={isLoginPromptOpen} onClose={() => setIsLoginPromptOpen(false)} />
    </div>
  );
}
